import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { IoDocumentText, IoCheckmarkCircle, IoTime, IoAdd } from 'react-icons/io5';
import { ApplicationProgress as ApplicationProgressType } from '../types';

interface ApplicationProgressProps {
  applications: ApplicationProgressType[];
  isLoading: boolean;
}

const ApplicationProgress: React.FC<ApplicationProgressProps> = ({ applications, isLoading }) => {
  const navigate = useNavigate();

  const getStatusBadgeColor = useCallback((status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'status-badge-green';
      case 'IN_PROGRESS':
        return 'status-badge-yellow';
      case 'PENDING':
        return 'status-badge-blue';
      default:
        return 'status-badge-gray';
    }
  }, []);

  const getStatusLabel = useCallback((status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'Submitted';
      case 'IN_PROGRESS':
        return 'In Progress';
      case 'PENDING':
        return 'Under Review';
      default:
        return status;
    }
  }, []);

  if (isLoading) {
    return (
      <div className="application-progress-card">
        <div className="card-header">
          <h2 className="card-title">Application Progress</h2>
        </div>
        <div className="loading-state">Loading applications...</div>
      </div>
    );
  }

  return (
    <div className="application-progress-card">
      <div className="card-header">
        <h2 className="card-title">Application Progress</h2>
        <select className="progress-filter">
          <option>All Applications</option>
          <option>In Progress</option>
          <option>Submitted</option>
          <option>Under Review</option>
        </select>
      </div>

      <div className="applications-list">
        {applications.length > 0 ? (
          applications.map((app) => (
            <div key={app.id} className="application-item">
              <div className="application-header">
                <div className="application-info">
                  <div className="application-icon">
                    <IoDocumentText className="application-icon-svg" />
                  </div>
                  <div>
                    <h3 className="application-name">{app.name}</h3>
                    <p className="application-deadline">Regular Decision • Deadline: {app.deadline}</p>
                  </div>
                </div>
                <span className={`status-badge ${getStatusBadgeColor(app.status)}`}>
                  {getStatusLabel(app.status)}
                </span>
              </div>
              <div className="application-progress-bar">
                <div className="progress-bar-bg">
                  <div
                    className="progress-bar-fill"
                    style={{ width: `${app.completion}%` }}
                  />
                </div>
                <span className="progress-percentage">{app.completion}%</span>
              </div>
              <div className="application-status-icons">
                <span className="status-icon-item">
                  <IoCheckmarkCircle className="status-icon-check" />
                  Common App Complete
                </span>
                <span className="status-icon-item">
                  <IoCheckmarkCircle className="status-icon-check" />
                  Essays Submitted
                </span>
                <span className="status-icon-item">
                  <IoTime className="status-icon-pending" />
                  Recommendations Pending
                </span>
              </div>
            </div>
          ))
        ) : (
          <div className="empty-state">
            No applications yet. Start by adding a university to your list.
          </div>
        )}
      </div>

      <button 
        className="add-application-button" 
        onClick={() => navigate('/college-list-builder')}
      >
        <IoAdd className="mr-2" /> Add New Application
      </button>
    </div>
  );
};

export default ApplicationProgress;